import { z } from 'zod';

import { ReportColumn, ReportSortBy, ReportTransactionRole, TransactionType } from './common';
import { ReportFormat } from './report';
import { DateStringSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { BookingStatus, ItemType } from '../booking/booking';
import { makeEndpoint, paginateResponseSchema, stringBooleanSchema } from '../common';
import { PaymentMethods } from '../payment-method/public';

export enum FinancialReportBookingType {
  PAID = 'paid',
  FREE = 'free',
}

export const FinancialReportV2Column = [
  ReportColumn.EXTERNAL_REFERENCE_ID, // ORG ONLY
  ReportColumn.BRAND_NAME, // ORG ONLY

  ReportColumn.CURRENCY,
  ReportColumn.EXPERIENCE_DATE,
  ReportColumn.TRANSACTION_DATE,

  ReportColumn.TRANSACTION_MONTH, // ORG ONLY

  ReportColumn.FIRST_NAME,
  ReportColumn.LAST_NAME,
  ReportColumn.EMAIL,
  ReportColumn.CONFIRMATION_CODE,
  ReportColumn.PAYMENT_TYPE,
  ReportColumn.PAYMENT_METHOD,
  ReportColumn.ACCOUNT,
  ReportColumn.EXPERIENCE_NAME,
  ReportColumn.PRICING_LABEL,
  ReportColumn.ITEM_TYPE,
  ReportColumn.QUANTITY,
  ReportColumn.START_TIME,
  ReportColumn.DURATION,
  ReportColumn.HOST,
  ReportColumn.TRANSACTION_ROLE,
  ReportColumn.PARTNER_NAME,
  ReportColumn.TOTAL,
  ReportColumn.DISCOUNT_AMOUNT,
  ReportColumn.SUBTOTAL,

  ReportColumn.GROSS_REVENUE, // ORG ONLY
  ReportColumn.NET_REVENUE, // ORG ONLY

  ReportColumn.TOTAL_PAID,
  ReportColumn.FX_ADJUSTMENT,
  ReportColumn.HOST_PAYOUT,
  ReportColumn.PROMOTER_PAYOUT,
  ReportColumn.CREDIT_CARD,
  ReportColumn.STRIPE_FX_FEE,
  ReportColumn.WAY_FEE,
  ReportColumn.PARTNER_PAYOUT,
  ReportColumn.NET_DEPOSITED,
  ReportColumn.DISCOUNT_CODE,
  ReportColumn.DATE_DEPOSITED,
  ReportColumn.NOTES,
  ReportColumn.INTERNAL_NOTES,
  ReportColumn.RESERVATION_ID,

  ReportColumn.TAXES,

  ReportColumn.CART_ID,
  ReportColumn.CREATED_BY,
  ReportColumn.CATEGORY,
  ReportColumn.LISTING_STATUS,
  ReportColumn.BOOKING_URL,
  ReportColumn.PROMOTER_SOURCE,
  ReportColumn.APPROVED_PROMOTERS,
] as const satisfies ReadonlyArray<ReportColumn>;

export type FinancialReportV2ColumnType = (typeof FinancialReportV2Column)[number];

export const financialColumnsV2Map = {
  [ReportColumn.EXTERNAL_REFERENCE_ID]: 'External Reference ID',
  [ReportColumn.BRAND_NAME]: 'Brand Name',

  [ReportColumn.CURRENCY]: 'Currency',
  [ReportColumn.EXPERIENCE_DATE]: 'Experience Date',
  [ReportColumn.TRANSACTION_DATE]: 'Transaction Date UTC',

  [ReportColumn.TRANSACTION_MONTH]: 'Transaction Month UTC',

  [ReportColumn.FIRST_NAME]: 'First Name',
  [ReportColumn.LAST_NAME]: 'Last Name',
  [ReportColumn.EMAIL]: 'Email',
  [ReportColumn.CONFIRMATION_CODE]: 'Confirmation Code',
  [ReportColumn.PAYMENT_TYPE]: 'Type',
  [ReportColumn.PAYMENT_METHOD]: 'Payment Method',
  [ReportColumn.ACCOUNT]: 'Account',
  [ReportColumn.EXPERIENCE_NAME]: 'Experience Name',
  [ReportColumn.PRICING_LABEL]: 'Item Label',
  [ReportColumn.ITEM_TYPE]: 'Item Type',
  [ReportColumn.QUANTITY]: 'Quantity',
  [ReportColumn.START_TIME]: 'Start Time',
  [ReportColumn.DURATION]: 'Duration',
  [ReportColumn.HOST]: 'Host',
  [ReportColumn.TRANSACTION_ROLE]: 'Transaction Role',
  [ReportColumn.PARTNER_NAME]: 'Partner Name',
  [ReportColumn.TOTAL]: 'Total',
  [ReportColumn.DISCOUNT_AMOUNT]: 'Discount Amount',
  [ReportColumn.SUBTOTAL]: 'Subtotal',

  [ReportColumn.GROSS_REVENUE]: 'Gross Revenue',
  [ReportColumn.NET_REVENUE]: 'Net Revenue',

  [ReportColumn.TOTAL_PAID]: 'Total Paid',
  [ReportColumn.FX_ADJUSTMENT]: 'FX Adjustment',
  [ReportColumn.HOST_PAYOUT]: 'Host Payout',
  [ReportColumn.PROMOTER_PAYOUT]: 'Promoter Payout',
  [ReportColumn.CREDIT_CARD]: 'Credit Card',
  [ReportColumn.STRIPE_FX_FEE]: 'Stripe FX Fee',
  [ReportColumn.WAY_FEE]: 'Way Fee',
  [ReportColumn.PARTNER_PAYOUT]: 'Partner Payout',
  [ReportColumn.NET_DEPOSITED]: 'Net Deposited',
  [ReportColumn.DISCOUNT_CODE]: 'Discount Code',
  [ReportColumn.DATE_DEPOSITED]: 'Date Deposited',
  [ReportColumn.NOTES]: 'Guest Notes',
  [ReportColumn.INTERNAL_NOTES]: 'Internal Notes',
  [ReportColumn.RESERVATION_ID]: 'Reservation ID',

  [ReportColumn.TAXES]: 'Taxes',

  [ReportColumn.CART_ID]: 'Cart ID',
  [ReportColumn.CREATED_BY]: 'Created By',
  [ReportColumn.CATEGORY]: 'Category',
  [ReportColumn.LISTING_STATUS]: 'Listing Status',
  [ReportColumn.BOOKING_URL]: 'Booking URL',
  [ReportColumn.PROMOTER_SOURCE]: 'Promoter Source',
  [ReportColumn.APPROVED_PROMOTERS]: 'Approved Promoters',
} as const satisfies Record<FinancialReportV2ColumnType, string>;

export const FinancialReportV2SortBy = [
  ReportSortBy.START_DATE_TIME,
  ReportSortBy.TRANSACTION_DATE,
] as const satisfies ReadonlyArray<ReportSortBy>;

const baseFinancialReportV2Filters = {
  from: z.union([DateStringSchema, z.string().datetime()]).optional(),
  to: z.union([DateStringSchema, z.string().datetime()]).optional(),
  showNegativeDiscountValues: stringBooleanSchema.optional(),
  transactionType: z.array(z.nativeEnum(TransactionType)).optional(),
  collectionIds: z.array(z.string().uuid()).optional(),
  eventId: z.string().uuid().optional(),
  experienceIds: z.array(z.string().uuid()).optional(),
  categoryIds: z.array(z.string().uuid()).optional(),
  hostIds: z.array(z.string().uuid()).optional(),
  confirmationCode: z.string().optional(),
  cartId: z.string().optional(),
  transactionId: z.string().optional(),
  paymentMethod: z.array(z.nativeEnum(PaymentMethods)).optional(),
  bookingType: z.array(z.nativeEnum(FinancialReportBookingType)).optional(),
  itemType: z.array(z.nativeEnum(ItemType)).optional(),
  bookingStatus: z.array(z.nativeEnum(BookingStatus)).optional(),
  dateDeposited: DateStringSchema.optional(),
  debug: stringBooleanSchema.optional(),
};

export type BaseFinancialReportV2FiltersKeys = keyof typeof baseFinancialReportV2Filters;

const financialReportV2FiltersSchema = z.object(baseFinancialReportV2Filters);

export type FinancialReportV2Filters = z.infer<typeof financialReportV2FiltersSchema>;

const BaseFinancialReportV2RequestSchema = z.object({
  params: financialReportV2FiltersSchema,
  sortBy: z.enum(FinancialReportV2SortBy),
  columns: z.array(z.enum(FinancialReportV2Column)),
  format: z.enum([ReportFormat.CSV, ReportFormat.PDF]).optional(),
});
export const BrandFinancialReportV2RequestSchema = BaseFinancialReportV2RequestSchema.extend({ brandId: z.string().uuid() });
export const OrganizationFinancialReportV2RequestSchema = BaseFinancialReportV2RequestSchema.extend({
  organizationId: z.string().uuid(),
});
export const FinancialReportV2RequestSchema = z.union([
  BrandFinancialReportV2RequestSchema,
  OrganizationFinancialReportV2RequestSchema,
]);
export type FinancialReportV2Request = z.infer<typeof FinancialReportV2RequestSchema>;

const PreviewFinancialV2RequestSchema = financialReportV2FiltersSchema.extend({
  limit: z.coerce.number().optional(),
  page: z.coerce.number().optional(),
  sortBy: z.enum(FinancialReportV2SortBy),
  organizationId: z.string().uuid().optional(),
});

export const financialReportV2ItemSchema = z.object({
  [ReportColumn.EXTERNAL_REFERENCE_ID]: z.string().nullable().optional(),
  [ReportColumn.BRAND_NAME]: z.string().optional(),

  [ReportColumn.CURRENCY]: z.string(),
  [ReportColumn.EXPERIENCE_DATE]: z.string(),
  [ReportColumn.TRANSACTION_DATE]: z.string(),

  [ReportColumn.TRANSACTION_MONTH]: z.string().optional(),

  [ReportColumn.FIRST_NAME]: z.string().nullable(),
  [ReportColumn.LAST_NAME]: z.string().nullable(),
  [ReportColumn.EMAIL]: z.string().nullable(),
  [ReportColumn.CONFIRMATION_CODE]: z.string().nullable(),
  [ReportColumn.PAYMENT_TYPE]: z.string(),

  [ReportColumn.PAYMENT_METHOD]: z.string(),
  [ReportColumn.ACCOUNT]: z.string().nullable(),
  [ReportColumn.EXPERIENCE_NAME]: z.string(),
  [ReportColumn.PRICING_LABEL]: z.string().nullable(),
  [ReportColumn.ITEM_TYPE]: z.string().nullable(),
  [ReportColumn.QUANTITY]: z.number().nullable(),
  [ReportColumn.START_TIME]: z.string().nullable(),
  [ReportColumn.DURATION]: z.string(),
  [ReportColumn.HOST]: z.string().nullable(),
  [ReportColumn.TRANSACTION_ROLE]: z.nativeEnum(ReportTransactionRole).nullable().optional(),
  [ReportColumn.PARTNER_NAME]: z.string().nullable().optional(),
  [ReportColumn.TOTAL]: z.number().nullable(),
  [ReportColumn.DISCOUNT_AMOUNT]: z.number().nullable(),
  [ReportColumn.SUBTOTAL]: z.number().nullable(),

  [ReportColumn.GROSS_REVENUE]: z.number().nullable().optional(),
  [ReportColumn.NET_REVENUE]: z.number().nullable().optional(),

  [ReportColumn.TOTAL_PAID]: z.number().nullable(),
  [ReportColumn.FX_ADJUSTMENT]: z.number().nullable().optional(),
  [ReportColumn.HOST_PAYOUT]: z.number().nullable(),
  [ReportColumn.PROMOTER_PAYOUT]: z.number().nullable().optional(),
  [ReportColumn.CREDIT_CARD]: z.number().nullable(),
  [ReportColumn.STRIPE_FX_FEE]: z.number().nullable().optional(),
  [ReportColumn.WAY_FEE]: z.number().nullable().optional(),
  [ReportColumn.PARTNER_PAYOUT]: z.number().nullable().optional(),
  [ReportColumn.NET_DEPOSITED]: z.number().nullable(),
  [ReportColumn.DISCOUNT_CODE]: z.string().nullable(),
  [ReportColumn.DATE_DEPOSITED]: z.string().nullable(),
  [ReportColumn.NOTES]: z.string().nullable(),
  [ReportColumn.INTERNAL_NOTES]: z.string().nullable(),
  [ReportColumn.RESERVATION_ID]: z.string().nullable(),
  [ReportColumn.TAXES]: z.record(z.number().nullable()),
  [ReportColumn.CART_ID]: z.string().nullable(),
  [ReportColumn.CREATED_BY]: z.string().nullable(),
  [ReportColumn.CATEGORY]: z.string().nullable(),
  [ReportColumn.LISTING_STATUS]: z.string().nullable(),
  [ReportColumn.BOOKING_URL]: z.string().nullable(),
  [ReportColumn.PROMOTER_SOURCE]: z.string().nullable(),
  [ReportColumn.APPROVED_PROMOTERS]: z.string().nullable(),
} satisfies Record<FinancialReportV2ColumnType, z.ZodType>);

export type FinancialReportV2Item = z.infer<typeof financialReportV2ItemSchema>;

const debugErrorsSchema = z
  .object({
    key: z.string(),
    expectedFromTransactions: z.number(),
    receivedFromReport: z.number(),
  })
  .or(
    z.object({
      knownIssue: z.string(),
    }),
  );

export type FinancialReportV2DebugError = z.infer<typeof debugErrorsSchema>;

export const PreviewFinancialV2Endpoint = makeEndpoint({
  method: 'GET',
  url: ({ query, brandId }: { query: z.input<typeof PreviewFinancialV2RequestSchema>; brandId: string }) =>
    `v1/brands/${brandId}/reports/financials/v2?${buildQueryString(query)}`,
  responseSchema: paginateResponseSchema(financialReportV2ItemSchema).merge(
    z.object({
      debugErrors: z.record(z.array(debugErrorsSchema)).optional(),
    }),
  ),
  requestSchema: z.void(),
  querySchema: PreviewFinancialV2RequestSchema,
});

export const webhookOnlyDataSchema = z.object({
  brandId: z.string(),
  experienceId: z.string(),
  bookingId: z.string().nullable(),
  bookingSourceUrl: z.string().url().nullable(),
  loyaltyPoints: z.number().nullable(),
  loyaltyMemberId: z.string().nullable(),
  externalAnalyticsUserId: z.string().nullable(),
  loyaltyTransactionId: z.string().nullable(),
});

export const webhookFinancialReportV2ItemSchema = financialReportV2ItemSchema
  .omit({
    [ReportColumn.EXPERIENCE_DATE]: true,
    [ReportColumn.TAXES]: true,
  })
  .extend({
    [ReportColumn.EXPERIENCE_DATE]: DateStringSchema,
    [ReportColumn.TAXES]: z.record(z.number()),
  })
  .merge(webhookOnlyDataSchema);

export type WebhookFinancialReportV2Item = z.infer<typeof webhookFinancialReportV2ItemSchema>;

export const FinancialReportV2WithHeaderSchema = z.object({
  'External Reference ID': z.string().optional(),
  'Brand Name': z.string().optional(),

  Currency: z.string().optional(),
  'Experience Date': z.string().optional(),
  'Transaction Date UTC': z.string().optional(),

  'Transaction Month UTC': z.string().optional(),

  'First Name': z.string().optional(),
  'Last Name': z.string().optional(),
  Email: z.string().optional(),
  'Confirmation Code': z.string().optional(),
  Type: z.string().optional(),
  'Payment Method': z.string().optional(),
  Account: z.string().optional(),
  'Experience Name': z.string().optional(),
  'Item Label': z.string().optional(),
  'Item Type': z.string().optional(),
  Quantity: z.string().optional(),
  'Start Time': z.string().optional(),
  Duration: z.string().optional(),
  Host: z.string().optional(),
  'Transaction Role': z.string().optional(),
  'Partner Name': z.string().optional(),
  Total: z.string().optional(),
  'Discount Amount': z.string().optional(),
  Subtotal: z.string().optional(),

  'Gross Revenue': z.string().optional(),
  'Net Revenue': z.string().optional(),

  'Total Paid': z.string().optional(),
  'FX Adjustment': z.string().optional(),
  'Host Payout': z.string().optional(),
  'Promoter Payout': z.string().optional(),
  'Credit Card': z.string().optional(),
  'Stripe FX Fee': z.string().optional(),
  'Way Fee': z.string().optional(),
  'Partner Payout': z.string().optional(),
  'Net Deposited': z.string().optional(),
  'Discount Code': z.string().optional(),
  'Date Deposited': z.string().optional(),
  'Guest Notes': z.string().optional(),
  'Internal Notes': z.string().optional(),
  'Reservation ID': z.string().optional(),
  'Cart ID': z.string().optional(),
  'Created By': z.string().optional(),
  Category: z.string().optional(),
  'Listing Status': z.string().optional(),
  'Booking URL': z.string().optional(),
  'Promoter Source': z.string().optional(),
  'Approved Promoters': z.string().optional(),
} satisfies Record<(typeof financialColumnsV2Map)[Exclude<keyof typeof financialColumnsV2Map, ReportColumn.TAXES>], z.ZodType>);
export type FinancialReportV2WithHeader = z.input<typeof FinancialReportV2WithHeaderSchema>;
